import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/lib/auth/store';
import { useBookingsStore } from '@/lib/stores/bookings-store';
import { useDataLoader } from '@/lib/utils/hooks';
import { ProfileHeader } from '@/components/profile/ProfileHeader';
import { ProfileStats } from '@/components/profile/ProfileStats';
import { ProfileOverview } from '@/components/profile/ProfileOverview';
import { ProfileDetails } from '@/components/profile/ProfileDetails';
import { ProfileSocial } from '@/components/profile/ProfileSocial';
import { CommunicationPreferences } from '@/components/profile/CommunicationPreferences';
import { BookingsSummary } from '@/components/profile/BookingsSummary';
import { QuickActions } from '@/components/profile/QuickActions';
import { FriendsExperiences } from '@/components/profile/FriendsExperiences';
import { getBookings } from '@/lib/utils/bookingFilters';

export function Profile() {
  const { user } = useAuth();
  const { bookings, fetchUserBookings } = useBookingsStore();

  // Load user's bookings when profile mounts
  useDataLoader(
    () => user ? fetchUserBookings(user.id) : Promise.resolve(),
    { reloadOnFocus: false } // Don't reload on tab focus
  );

  // Get recent bookings for summary (explicit filtering)
  const recentBookings = getBookings(bookings, { 
    status: 'confirmed', 
    limit: 3 
  });

  if (!user) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-sand-400">Please sign in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-earth-900">
      <div className="pt-20 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto px-4"
        >
          <div className="relative">
            <ProfileHeader user={user} isOwnProfile={true} />
            
            <div className="mt-32 sm:mt-24 md:mt-16 space-y-6 sm:space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                {/* BookingsSummary receives pre-filtered recent confirmed bookings */}
                <BookingsSummary bookings={recentBookings} user={user} />
                <FriendsExperiences />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                  <ProfileDetails user={user} />
                  <ProfileSocial user={user} />
                </div>
                <div className="space-y-4 sm:space-y-6">
                  <QuickActions user={user} />
                  <CommunicationPreferences />
                  <InterestsSection user={user} isOwnProfile={true} />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function InterestsSection({ user, isOwnProfile }: { user: any, isOwnProfile: boolean }) {
  const [editing, setEditing] = React.useState(false);
  const [interests, setInterests] = React.useState(user.interests || []);
  const allInterests = [
    'Adventure', 'Art & Culture', 'Cooking', 'Dance', 'Fashion', 'Fitness', 'Food & Drink',
    'History', 'Language', 'Music', 'Nature', 'Photography', 'Sports', 'Technology',
    'Travel', 'Wellness', 'Writing', 'Wine', 'Luxury', 'Networking', 'Wellbeing', 'Outdoors',
  ];
  const toggleInterest = (interest: string) => {
    setInterests(prev => prev.includes(interest)
      ? prev.filter(i => i !== interest)
      : [...prev, interest]
    );
  };
  return (
    <div className="bg-earth-800/50 p-6 rounded-lg mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-display font-bold text-sand-100">Interests</h3>
        {isOwnProfile && (
          <button
            className="text-sand-400 hover:text-sand-200 text-sm underline"
            onClick={() => setEditing(e => !e)}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
        )}
      </div>
      {editing ? (
        <div className="flex flex-wrap gap-2">
          {allInterests.map(interest => (
            <button
              key={interest}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${interests.includes(interest)
                ? 'bg-sand-200 text-earth-900 border-sand-200'
                : 'bg-earth-900 text-sand-300 border-sand-700 hover:bg-sand-800 hover:text-sand-100'}`}
              onClick={() => toggleInterest(interest)}
              type="button"
            >
              {interest}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {interests.length === 0 ? (
            <span className="text-sand-400">No interests selected yet.</span>
          ) : (
            interests.map((interest: string) => (
              <span
                key={interest}
                className="px-4 py-2 rounded-full bg-sand-200 text-earth-900 text-sm font-medium border border-sand-200"
              >
                {interest}
              </span>
            ))
          )}
        </div>
      )}
    </div>
  );
}